import { api } from '@/api';
import router from '@/router';
import { getLocalToken, getLocalRefreshToken, removeLocalToken, removeLocalRefreshToken, saveLocalToken, saveLocalRefreshToken, removeLastActivity, getLastActivity, setActiveUser, saveLocalAccountName, listStoredAccounts, hardNavigate } from '@/utils';
import { getStoreAccessors } from 'typesafe-vuex';
import { ActionContext } from 'vuex';
import { State } from '../state';
import { i18n } from '@/plugins/i18n';
import {
  commitAddNotification,
  commitRemoveNotification,
  commitSetLoggedIn,
  commitSetLogInError,
  commitSetLogInAccountLocked,
  commitSetInactivityLocked,
  commitSetToken,
  commitSetUserProfile,
  commitSetCalendars,
  commitSetPlanningConfiguration,
  commitSetPhysiotherapyUsers
} from './mutations';
import { AppNotification, MainState } from './state';

type MainContext = ActionContext<MainState, State>;

export const actions = {
  async actionLogIn(context: MainContext, payload: { username: string; password: string, otp: string | null }) {
    try {
      commitSetLogInAccountLocked(context, false);
      const response = await api.logInGetToken(payload.username, payload.password, payload.otp);
      const token = response.data.access_token;
      if (token) {
        commitSetToken(context, token);
        commitSetLoggedIn(context, true);
        commitSetLogInError(context, false);
        await dispatchGetUserProfile(context);
        if (context.state.userProfile?.email) {
          saveLocalToken(token, context.state.userProfile.email);
          setActiveUser(context.state.userProfile.email);
          saveLocalAccountName(context.state.userProfile.email, context.state.userProfile.full_name);
          if (response.data.refresh_token) {
            saveLocalRefreshToken(response.data.refresh_token, context.state.userProfile.email);
          }
        }
        await dispatchRouteLoggedIn(context);
        commitAddNotification(context, { content: String(i18n.t('login.loggedIn')), color: 'success' });
      } else {
        await dispatchLogOut(context);
      }
    } catch (err: any) {
      if (err?.response?.status === 423) {
        commitSetLogInAccountLocked(context, true);
        commitSetLogInError(context, false);
      } else {
        commitSetLogInError(context, true);
        commitSetLogInAccountLocked(context, false);
      }
      await dispatchLogOut(context);
    }
  },
  async actionLogInOnBehalfOf(context: MainContext, onBehalfOf: string) {
    try {
      const response = await api.getTokenOnBehalfOf(onBehalfOf);
      const token = response.data.access_token;
      if (token) {
        commitSetToken(context, token);
        commitSetLoggedIn(context, true);
        commitSetLogInError(context, false);
        await dispatchGetUserProfile(context);
        if (context.state.userProfile?.email) {
          saveLocalToken(token, context.state.userProfile.email);
          setActiveUser(context.state.userProfile.email);
        }
        await dispatchRouteLoggedIn(context);
        commitAddNotification(context, { content: String(i18n.t('login.loggedIn')), color: 'success' });
      } else {
        await dispatchLogOut(context);
      }
    } catch (err) {
      commitSetLogInError(context, true);
      await dispatchLogOut(context);
    }
  },
  async actionGetUserProfile(context: MainContext ) {
    try {
      const response = await api.getMe();
      if (response.data) {
        commitSetUserProfile(context, response.data);
        saveLocalAccountName(response.data.email, response.data.full_name);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetUserProfileWithGoogleValidation(context: MainContext ) {
    try {
      const response = await api.getMe(true);
      if (response.data) {
        commitSetUserProfile(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetPhysiotherapists(context: MainContext) {
    try {
      const physiotherapyUsers = await api.getPhysiotherapyUsers();
      if (physiotherapyUsers.data) {
        commitSetPhysiotherapyUsers(context, physiotherapyUsers.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetPlanningConfiguration(context: MainContext) {
    try {
      const response = await api.getPlannerConfiguration();
      if (response.data) {
        commitSetPlanningConfiguration(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionUpdatePlanningConfiguration(context: MainContext, payload) {
    const response = (await Promise.all([
      api.updatePlannerConfiguration(payload),
      await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
    ]))[0];
    commitSetPlanningConfiguration(context, response.data);
  },
  async actionUpdateUserProfile(context: MainContext, payload) {
    try {
      const loadingNotification = { content: String(i18n.t('common.loading')), showProgress: true };
      commitAddNotification(context, loadingNotification);
      const response = (await Promise.all([
        api.updateMe(payload),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]))[0];
      commitSetUserProfile(context, response.data);
      saveLocalAccountName(response.data.email, response.data.full_name);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: String(i18n.t('profile.updated')), color: 'success' });
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetGoogleCalendars(context: MainContext) {
    try {
      const calendars = await api.getCalendars();
      if (calendars.data) {
        commitSetCalendars(context, calendars.data);
      }
      return calendars.data;
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionCheckLoggedIn(context: MainContext) {
    if (!context.state.isLoggedIn) {
      let token = context.state.token;
      if (!token) {
        const localToken = getLocalToken();
        if (localToken) {
          commitSetToken(context, localToken);
          token = localToken;
        }
      }
      if (token) {
        try {
          const response = await api.getMe();
          commitSetLoggedIn(context, true);
          commitSetUserProfile(context, response.data);
          // Migrate legacy token or ensure token is stored for this user
          if (response.data?.email) {
            saveLocalToken(token, response.data.email);
            setActiveUser(response.data.email);
            saveLocalAccountName(response.data.email, response.data.full_name);
          }
          const email = response.data?.email;
          const limitMinutes = response.data?.setting_lock_inactive_minutes;
          if (email && limitMinutes) {
            const lastActivity = getLastActivity(email);
            if (lastActivity && (Date.now() - lastActivity) / 60000 > limitMinutes) {
              commitSetInactivityLocked(context, true);
            }
          }
        } catch (error) {
          await dispatchRemoveLogIn(context);
        }
      } else {
        await dispatchRemoveLogIn(context);
      }
    }
  },
  async actionRemoveLogIn(context: MainContext) {
    removeLastActivity(context.state.userProfile?.email);
    removeLocalToken(context.state.userProfile?.email);
    removeLocalRefreshToken(context.state.userProfile?.email);
    commitSetToken(context, '');
    commitSetLoggedIn(context, false);
  },
  async actionLogOut(context: MainContext) {
    await dispatchRemoveLogIn(context);
    await dispatchRouteLogOut(context);
  },
  async actionUserLogOut(context: MainContext) {
    const refreshToken = getLocalRefreshToken(context.state.userProfile?.email);
    if (refreshToken) {
      try {
        await api.logout(refreshToken);
      } catch {
        // server-side revocation failed; continue with local cleanup
      }
    }
    await dispatchRemoveLogIn(context);
    // Another account is still signed in on this browser: continue as that one instead of showing the login page.
    const remaining = listStoredAccounts();
    if (remaining.length > 0) {
      setActiveUser(remaining[0].email);
      hardNavigate(router.resolve('/main').href);
      return;
    }
    await dispatchRouteLogOut(context);
    commitAddNotification(context, { content: String(i18n.t('login.loggedOut')), color: 'success' });
  },
  actionRouteLogOut(_context: MainContext) {
    if (router.currentRoute.path !== '/login') {
      router.push('/login').catch(() => { });
    }
  },
  async actionCheckApiError(context: MainContext, payload: any) {
    if (payload.response?.status === 401 || payload.response?.status === 403) {
      await dispatchLogOut(context);
    }
  },
  actionRouteLoggedIn(_context: MainContext) {
    if (router.currentRoute.path === '/login' || router.currentRoute.path === '/') {
      router.push('/main').catch(() => { });
    }
  },
  async removeNotification(context: MainContext, payload: { notification: AppNotification, timeout: number }) {
    return new Promise((resolve) => {
      setTimeout(() => {
        commitRemoveNotification(context, payload.notification);
        resolve(true);
      }, payload.timeout);
    });
  },
  async passwordRecovery(context: MainContext, payload: { username: string }) {
    const loadingNotification = { content: String(i18n.t('login.passwordResetEmailSending')), showProgress: true };
    try {
      commitAddNotification(context, loadingNotification);
      await Promise.all([
        api.passwordRecovery(payload.username),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: String(i18n.t('login.passwordResetEmailSent')), color: 'success' });
      await dispatchLogOut(context);
    } catch (error) {
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { color: 'error', content: String(i18n.t('login.usernameIncorrect')) });
    }
  },
  async resetPassword(context: MainContext, payload: { password: string, token: string }) {
    const loadingNotification = { content: String(i18n.t('login.passwordResetting')), showProgress: true };
    try {
      commitAddNotification(context, loadingNotification);
      await Promise.all([
        api.resetPassword(payload.password, payload.token),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { content: String(i18n.t('login.passwordReset')), color: 'success' });
      await dispatchLogOut(context);
    } catch (error) {
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, { color: 'error', content: String(i18n.t('login.passwordResetError')) });
    }
  },
  async actionUpdateGoogleCalendar(context: MainContext, calendar_id: string) {
    try {
      await Promise.all([
        api.putCalendar(calendar_id),
        new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]);
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGoogleAuthorizationToken(context: MainContext, code: string) {
    try {
      await Promise.all([
        api.setGoogleAuthorizationToken(code),
        new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]);
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionDeleteGoogle(context: MainContext) {
    try {
      await api.deleteGoogle();
      commitSetCalendars(context, []);
      await dispatchGetUserProfile(context);
    } catch (error) {
      await dispatchCheckApiError(context, error);
      throw error;
    }
  },

  async actionRegisterEHealth(context: MainContext, payload: { ssin: string, passphrase: string, certificate: File }) {
    try {
      await api.registerEHealth(payload.ssin, payload.passphrase, payload.certificate);
      commitAddNotification(context, { content: String(i18n.t('eHealth.configured')), color: 'success' });
    } catch (error: any) {
      if (error.response) {
        let error_message = error.response.data.detail;
        if (error_message === 'Invalid certificate or wrong password') {
          error_message = String(i18n.t('eHealth.invalidPasswordOrCertificate'))
        }
        if (error_message.includes('DerInputStream')) {
          error_message = String(i18n.t('eHealth.invalidCertificateFile'))
        }
        commitAddNotification(context, { color: 'error', content: `${i18n.t('eHealth.errorPrefix')}: ${error_message}` });
      }

    }
  },
};

const { dispatch } = getStoreAccessors<MainState | any, State>('');

export const dispatchCheckApiError = dispatch(actions.actionCheckApiError);
export const dispatchCheckLoggedIn = dispatch(actions.actionCheckLoggedIn);
export const dispatchGetUserProfile = dispatch(actions.actionGetUserProfile);
export const dispatchGetUserProfileWithGoogleValidation = dispatch(actions.actionGetUserProfileWithGoogleValidation);
export const dispatchGetPhysiotherapists = dispatch(actions.actionGetPhysiotherapists);
export const dispatchGetPlanningConfiguration = dispatch(actions.actionGetPlanningConfiguration);
export const dispatchUpdatePlanningConfiguration = dispatch(actions.actionUpdatePlanningConfiguration);
export const dispatchLogIn = dispatch(actions.actionLogIn);
export const dispatchLogOut = dispatch(actions.actionLogOut);
export const dispatchUserLogOut = dispatch(actions.actionUserLogOut);
export const dispatchRemoveLogIn = dispatch(actions.actionRemoveLogIn);
export const dispatchRouteLoggedIn = dispatch(actions.actionRouteLoggedIn);
export const dispatchRouteLogOut = dispatch(actions.actionRouteLogOut);
export const dispatchUpdateUserProfile = dispatch(actions.actionUpdateUserProfile);
export const dispatchRemoveNotification = dispatch(actions.removeNotification);
export const dispatchPasswordRecovery = dispatch(actions.passwordRecovery);
export const dispatchResetPassword = dispatch(actions.resetPassword);
export const dispatchUpdateCalendar = dispatch(actions.actionUpdateGoogleCalendar);
export const dispatchGoogleAuthorizationToken = dispatch(actions.actionGoogleAuthorizationToken);
export const dispatchRegisterEHealth = dispatch(actions.actionRegisterEHealth);
export const dispatchLogInOnBehalfOf = dispatch(actions.actionLogInOnBehalfOf);
export const dispatchGetGoogleCalendars = dispatch(actions.actionGetGoogleCalendars);
export const dispatchDeleteGoogle = dispatch(actions.actionDeleteGoogle);